import { useState, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import SnapshotControl from "@/components/SnapshotControl";
import {
  Loader2, Sparkles, CheckCircle2, AlertTriangle, XCircle, Scale, ShieldAlert,
  ChevronDown, ChevronRight, Plus, FileCheck2, Store,
} from "lucide-react";

const money = (n) => `$${Math.round(Number(n) || 0).toLocaleString()}`;

const STATUS_STYLE = {
  "FULLY PRICED": "bg-emerald-500/15 text-emerald-500 border-emerald-500/40",
  "PARTIALLY PRICED": "bg-amber-500/15 text-amber-500 border-amber-500/40",
  "ALLOWANCE": "bg-sky-500/15 text-sky-500 border-sky-500/40",
  "LUMP SUM": "bg-orange-500/15 text-orange-500 border-orange-500/40",
  "BUDGETARY": "bg-violet-500/15 text-violet-400 border-violet-500/40",
  "MISSING": "bg-red-500/15 text-red-500 border-red-500/40",
  "NEEDS REVIEW": "bg-yellow-500/15 text-yellow-500 border-yellow-500/40",
  "NOT APPLICABLE": "bg-secondary text-muted-foreground border-border",
};
const RESOLVE_OPTIONS = ["Resolved", "Accepted as Allowance", "Accepted as Lump Sum", "Excluded", "Not Applicable", "Needs Vendor Quote", "Needs Manual Pricing"];

const Scorecard = ({ label, value, tone }) => (
  <div className="rounded-xl border border-border p-3">
    <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{label}</div>
    <div className={`text-lg font-bold ${tone || ""}`}>{value}</div>
  </div>
);

function AddItemInline({ projectId, code, description, resolveKey, onDone, label = "Add cost", testid = "unpriced-addcost" }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ code: code || "01", quantity: 1, unit: "ea", labor_rate: 0, material_rate: 0, equipment_rate: 0, description });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/projects/${projectId}/bid-review/add-item`, { ...f, code: String(f.code || "01").trim(), resolve_key: resolveKey, quantity: Number(f.quantity), labor_rate: Number(f.labor_rate), material_rate: Number(f.material_rate), equipment_rate: Number(f.equipment_rate) });
      toast.success("Line item added & estimate recalculated");
      onDone();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  if (!open) return <button data-testid={testid} onClick={() => setOpen(true)} className="inline-flex items-center gap-1 rounded border border-primary/40 text-primary px-2 py-1 text-xs font-semibold hover:bg-primary/10"><Plus className="h-3 w-3" /> {label}</button>;
  return (
    <div className="mt-2 rounded-lg border border-border p-2 space-y-2 bg-secondary/40">
      <input value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className="w-full rounded border border-input bg-card px-2 py-1 text-xs" data-testid="additem-description" />
      <div className="grid grid-cols-6 gap-1 text-xs">
        {[["code", "CSI Div"], ["quantity", "Qty"], ["unit", "Unit"], ["labor_rate", "Labor$"], ["material_rate", "Matl$"], ["equipment_rate", "Equip$"]].map(([k, l]) => (
          <label key={k} className="flex flex-col gap-0.5"><span className="text-muted-foreground">{l}</span>
            <input value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="rounded border border-input bg-card px-1 py-0.5" data-testid={`additem-${k}`} /></label>
        ))}
      </div>
      <div className="flex gap-2">
        <button onClick={submit} disabled={busy} className="rounded bg-primary text-primary-foreground px-2 py-1 text-xs font-semibold disabled:opacity-60" data-testid="additem-save">{busy ? "Adding…" : "Save & recalc"}</button>
        <button onClick={() => setOpen(false)} className="text-xs text-muted-foreground">Cancel</button>
      </div>
    </div>
  );
}

function PriceOptions({ sug, appliedTier, onApply, onUnapply, idPrefix }) {
  if (!sug?.options?.length) return null;
  const applied = appliedTier;
  return (
    <div className="mt-2.5" data-testid={`${idPrefix}-suggestions`}>
      <div className="text-[10px] font-mono uppercase tracking-wider text-emerald-500 font-bold mb-1.5 flex items-center gap-1">
        <Store className="h-3 w-3" /> {applied ? "Applied local pricing — tap another tier to replace" : "Suggested local pricing — tap to apply"}
      </div>
      <div className="grid sm:grid-cols-3 gap-2">
        {sug.options.map((opt, oi) => {
          const unit = (Number(opt.labor_rate) || 0) + (Number(opt.material_rate) || 0) + (Number(opt.equipment_rate) || 0);
          const line = unit * (Number(sug.quantity) || 1);
          const isApplied = applied === opt.tier;
          return (
            <div key={oi} data-testid={`${idPrefix}-option-${oi}`} className={`rounded-lg border p-2.5 flex flex-col ${isApplied ? "border-emerald-500 bg-emerald-500/15 ring-1 ring-emerald-500/40" : "border-emerald-500/30 bg-emerald-500/5"}`}>
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-mono uppercase tracking-wider text-emerald-500 font-bold">{opt.tier}</span>
                <span className="font-mono font-bold text-sm">{money(unit)}<span className="text-[10px] text-muted-foreground">/{sug.unit || "ea"}</span></span>
              </div>
              {opt.label && <div className="text-xs font-medium mt-0.5 leading-snug">{opt.label}</div>}
              <div className="text-[11px] text-muted-foreground mt-0.5">{sug.quantity || 1} {sug.unit || "ea"} · <span className="font-semibold text-foreground">{money(line)}</span> line</div>
              {opt.source && <div className="text-[10px] text-muted-foreground mt-1 flex items-start gap-1"><Store className="h-3 w-3 shrink-0 mt-0.5" /><span className="line-clamp-2">{opt.source}</span></div>}
              {isApplied ? (
                <div className="mt-2 flex items-center gap-1">
                  <span data-testid={`${idPrefix}-applied-${oi}`} className="flex-1 inline-flex items-center justify-center gap-1 rounded bg-emerald-500 text-white px-2 py-1 text-xs font-bold"><CheckCircle2 className="h-3 w-3" /> Applied</span>
                  <button onClick={onUnapply} data-testid={`${idPrefix}-unapply-${oi}`} className="inline-flex items-center justify-center gap-1 rounded border border-red-500/40 text-red-500 px-2 py-1 text-xs font-semibold hover:bg-red-500/10 transition-colors">Un-apply</button>
                </div>
              ) : (
                <button onClick={() => onApply(opt)} data-testid={`${idPrefix}-apply-${oi}`} className="mt-2 inline-flex items-center justify-center gap-1 rounded bg-emerald-500 text-white px-2 py-1 text-xs font-semibold hover:bg-emerald-600 transition-colors">
                  <Plus className="h-3 w-3" /> {applied ? "Replace" : "Apply"}
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function BidReviewTab({ projectId }) {
  const qc = useQueryClient();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [openCsi, setOpenCsi] = useState(true);
  const [expandRecon, setExpandRecon] = useState(false);

  const fetchReport = async () => {
    try {
      const { data } = await api.get(`/projects/${projectId}/bid-review`);
      if (data?.reconciliation) { setReport(data); return data; }
    } catch { /* ignore */ }
    return null;
  };
  useEffect(() => {
    (async () => {
      const d = await fetchReport();
      if (d?.stale) run(); // estimate changed since last analysis → auto re-run
    })();
    /* eslint-disable-next-line */
  }, [projectId]);

  const run = async () => {
    setLoading(true);
    try { const { data } = await api.post(`/projects/${projectId}/bid-review`); setReport(data); }
    catch (e) { toast.error(apiErr(e)); } finally { setLoading(false); }
  };
  const resolve = async (key, status) => {
    try { const { data } = await api.post(`/projects/${projectId}/bid-review/resolve`, { key, status }); setReport((r) => ({ ...r, resolutions: data.resolutions })); toast.success("Marked"); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const [pricing, setPricing] = useState(false);
  const suggestPrices = async () => {
    setPricing(true);
    try {
      await api.post(`/projects/${projectId}/bid-review/price-suggestions`);
      const started = Date.now();
      const poll = async () => {
        try {
          const { data } = await api.get(`/projects/${projectId}/bid-review`);
          const status = data?.price_suggestions_status;
          if (status === "running" && Date.now() - started < 210000) {
            setTimeout(poll, 4000);
            return;
          }
          setReport((rr) => ({ ...rr, price_suggestions: data.price_suggestions || [], price_sources: data.price_sources || [] }));
          setPricing(false);
          if (status === "error") toast.error("Couldn't fetch local prices — please try again");
          else toast.success(`Priced ${(data.price_suggestions || []).length} item(s) from local sources`);
        } catch (e) { setPricing(false); toast.error(apiErr(e)); }
      };
      setTimeout(poll, 4000);
    } catch (e) { toast.error(apiErr(e)); setPricing(false); }
  };
  const applyOption = async (key, sug, opt) => {
    try {
      await api.post(`/projects/${projectId}/bid-review/add-item`, {
        code: String(sug?.division || "01").trim(),
        description: (sug?.item || "").trim(),
        quantity: Number(sug?.quantity || 1), unit: sug?.unit || "ea",
        labor_rate: Number(opt.labor_rate || 0), material_rate: Number(opt.material_rate || 0), equipment_rate: Number(opt.equipment_rate || 0),
        resolve_key: key, tier: opt.tier,
      });
      toast.success(`Applied ${opt.tier} price — estimate updated`);
      fetchReport();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const unapply = async (key) => {
    try {
      await api.post(`/projects/${projectId}/bid-review/unapply`, { resolve_key: key });
      toast.success("Removed from estimate");
      fetchReport();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const r = report;
  const rec = r?.reconciliation;
  const exp = r?.exposure;
  const ct = r?.cost_types;
  const res = r?.resolutions || {};
  const suggestions = r?.price_suggestions || [];
  const priceSources = r?.price_sources || [];
  const appliedTier = r?.applied_tier || {};
  const unpricedItemSet = new Set((r?.unpriced_scope || []).map((u) => (u.item || "").trim().toLowerCase()));
  const findSug = (u, i) => suggestions.find((sg) => (sg.item || "").trim().toLowerCase() === (u.item || "").trim().toLowerCase()) || suggestions[i];

  return (
    <div className="space-y-4" data-testid="bid-review-tab">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-bold flex items-center gap-2"><FileCheck2 className="h-4 w-4 text-primary" /> Bid Review & Scope Completeness</h3>
          <p className="text-xs text-muted-foreground">Audits CSI coverage, flags allowances, lump sums & potentially unpriced scope, and reconciles the total.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <SnapshotControl projectId={projectId} scope="bidreview" onRestored={async () => { await fetchReport(); qc.invalidateQueries({ queryKey: ["boq", projectId] }); }} />
          <button data-testid="run-bid-review" onClick={run} disabled={loading} className="inline-flex items-center gap-2 rounded-lg bg-primary text-primary-foreground px-4 py-2 text-sm font-semibold disabled:opacity-60">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} {r ? "Re-run review" : "Run bid review"}
          </button>
        </div>
      </div>

      {!r && !loading && <div className="rounded-xl border border-dashed border-border py-16 text-center text-muted-foreground text-sm">Run a bid review to audit scope coverage and reconcile the total.</div>}

      {r?.stale && !loading && (
        <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 text-amber-500 px-4 py-2 text-sm flex items-center gap-2" data-testid="stale-banner">
          <AlertTriangle className="h-4 w-4" /> The estimate changed since the last scope analysis — figures below are live; re-run to refresh division statuses.
        </div>
      )}
      {loading && r && (
        <div className="rounded-lg border border-border px-4 py-2 text-sm flex items-center gap-2 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Updating scope analysis…</div>
      )}

      {r && (
        <>
          {/* Reconciliation */}
          <div className="rounded-xl border border-border p-4" data-testid="reconciliation-card">
            <button onClick={() => setExpandRecon((v) => !v)} className="flex w-full items-center justify-between">
              <span className="font-semibold flex items-center gap-2"><Scale className="h-4 w-4 text-primary" /> Grand Total Reconciliation</span>
              <span className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-bold ${rec.pass ? "bg-emerald-500/15 text-emerald-500 border-emerald-500/40" : "bg-red-500/15 text-red-500 border-red-500/40"}`}>
                {rec.pass ? <CheckCircle2 className="h-3.5 w-3.5" /> : <XCircle className="h-3.5 w-3.5" />}
                {rec.pass ? "PASS" : `ERROR — ${money(rec.difference)} diff`}
                {expandRecon ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
              </span>
            </button>
            {expandRecon && (
              <div className="mt-3 text-sm space-y-1 font-mono" data-testid="reconciliation-detail">
                {[["Direct Costs", rec.direct_costs], [`Overhead (${rec.markups.overhead_pct}%)`, rec.overhead], [`Profit (${rec.markups.profit_pct}%)`, rec.profit], [`Contingency (${rec.markups.contingency_pct}%)`, rec.contingency]].map(([l, v]) => (
                  <div key={l} className="flex justify-between"><span className="text-muted-foreground">{l}</span><span>{money(v)}</span></div>
                ))}
                <div className="flex justify-between border-t border-border pt-1"><span className="text-muted-foreground">Subtotal (pre-tax)</span><span>{money(rec.pre_tax)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Tax ({rec.markups.tax_pct}%)</span><span>{money(rec.tax)}</span></div>
                <div className="flex justify-between border-t border-border pt-1 font-bold"><span>Grand Total</span><span className="text-primary">{money(rec.grand_total)}</span></div>
              </div>
            )}
          </div>

          {/* Scorecards */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="completeness-scorecards">
            <Scorecard label="Scope Coverage" value={`${exp.scope_coverage_pct}%`} tone="text-primary" />
            <Scorecard label="Fully Supported" value={`${exp.fully_supported_pct}%`} tone="text-emerald-500" />
            <Scorecard label="Allowance Exposure" value={money(exp.allowance_exposure)} tone="text-sky-500" />
            <Scorecard label="Lump-Sum Exposure" value={money(exp.lump_sum_exposure)} tone="text-orange-500" />
            <Scorecard label="Unpriced Scope" value={`${r.unpriced_scope.length} cat.`} tone="text-red-500" />
            <Scorecard label="Reconciliation" value={rec.pass ? "PASS" : "ERROR"} tone={rec.pass ? "text-emerald-500" : "text-red-500"} />
          </div>

          {/* Potentially Unpriced Scope — major feature */}
          <div className="rounded-xl border-2 border-red-500/40 bg-red-500/5 p-4" data-testid="unpriced-scope">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
              <div className="font-bold flex items-center gap-2 text-red-500"><ShieldAlert className="h-4 w-4" /> Potentially Unpriced Scope</div>
              {(r.unpriced_scope.length > 0 || r.divisions?.some((d) => ["MISSING", "PARTIALLY PRICED"].includes(d.status))) && (
                <button data-testid="suggest-prices-btn" onClick={suggestPrices} disabled={pricing}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-500 text-white px-3 py-1.5 text-xs font-bold hover:bg-emerald-600 transition-colors disabled:opacity-60">
                  {pricing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Store className="h-3.5 w-3.5" />} {pricing ? "Pricing from local sources…" : "Suggest prices (local + CSI gaps)"}
                </button>
              )}
            </div>
            {r.unpriced_scope.length === 0 ? (
              <p className="text-sm text-muted-foreground">No clearly-required scope appears to be missing. Review allowances & lump sums below.</p>
            ) : (
              <div className="space-y-2">
                {r.unpriced_scope.map((u, i) => {
                  const key = `unp_${i}`;
                  const done = res[key];
                  const sug = findSug(u, i);
                  return (
                    <div key={i} className="rounded-lg border border-border bg-card p-3" data-testid={`unpriced-${i}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="font-semibold text-sm">Div {u.division} · {u.item}</div>
                          <div className="text-xs text-muted-foreground">{u.reason}</div>
                          <div className="text-[11px] text-muted-foreground mt-0.5 italic">Scope identified but insufficient information to price — add a cost or mark it.</div>
                        </div>
                        {done && <span className="shrink-0 text-xs font-semibold text-emerald-500">{done.status}</span>}
                      </div>
                      {sug?.options?.length > 0 && (
                        <PriceOptions sug={sug} appliedTier={appliedTier[key]} idPrefix={`price-${i}`}
                          onApply={(opt) => applyOption(key, sug, opt)} onUnapply={() => unapply(key)} />
                      )}
                      <div className="flex flex-wrap items-center gap-2 mt-2">
                        <AddItemInline projectId={projectId} code={u.division} description={u.item} resolveKey={key} onDone={fetchReport} />
                        <select data-testid={`resolve-${i}`} value={done?.status || ""} onChange={(e) => resolve(key, e.target.value)} className="rounded border border-input bg-card px-2 py-1 text-xs">
                          <option value="" disabled>Mark as…</option>
                          {RESOLVE_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                        </select>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            {priceSources.length > 0 && (
              <div className="mt-3 pt-3 border-t border-red-500/20">
                <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mb-1">Pricing sources</div>
                <div className="flex flex-wrap gap-x-3 gap-y-1">
                  {priceSources.map((s, i) => (
                    <a key={i} href={s.url} target="_blank" rel="noreferrer" data-testid={`price-source-${i}`} className="text-[11px] text-primary hover:underline truncate max-w-[220px]">↗ {s.label || s.url}</a>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Plan vs estimate cross-check */}
          {r.plan_crosscheck?.length > 0 && (
            <div className="rounded-xl border-2 border-sky-500/40 bg-sky-500/5 p-4" data-testid="plan-crosscheck">
              <div className="font-bold flex items-center gap-2 mb-1 text-sky-500"><Scale className="h-4 w-4" /> Plan vs Estimate Cross-Check</div>
              <p className="text-xs text-muted-foreground mb-2">Measurable scope shown on the 3D plan/blueprint compared against what the estimate prices.</p>
              <div className="space-y-2">
                {r.plan_crosscheck.map((c, i) => {
                  const tone = c.severity === "high" ? "text-red-500 border-red-500/40" : c.severity === "med" ? "text-amber-500 border-amber-500/40" : "text-sky-500 border-sky-500/40";
                  const key = `cc_${i}`;
                  const done = res[key];
                  const gapDesc = `${c.category}${c.note ? ` — ${c.note}` : ""}`;
                  return (
                    <div key={i} className="rounded-lg border border-border bg-card p-3" data-testid={`crosscheck-${i}`}>
                      <div className="flex items-center justify-between gap-3">
                        <span className="font-semibold text-sm">{c.category}</span>
                        <div className="flex items-center gap-2 shrink-0">
                          {done && <span className="text-xs font-semibold text-emerald-500">{done.status}</span>}
                          <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-bold ${tone}`}>{(c.severity || "low").toUpperCase()}</span>
                        </div>
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">Plan: <span className="text-foreground font-medium">{c.plan_qty}</span> · Estimate: <span className="text-foreground font-medium">{c.estimate_qty}</span></div>
                      {c.note && <div className="text-xs text-muted-foreground mt-0.5">{c.note}</div>}
                      <div className="flex flex-wrap items-center gap-2 mt-2">
                        <AddItemInline projectId={projectId} code={c.division || "01"} description={gapDesc} resolveKey={key} onDone={fetchReport} label="Price this gap" testid={`crosscheck-price-${i}`} />
                        <select data-testid={`crosscheck-resolve-${i}`} value={done?.status || ""} onChange={(e) => resolve(key, e.target.value)} className="rounded border border-input bg-card px-2 py-1 text-xs">
                          <option value="" disabled>Mark as…</option>
                          {RESOLVE_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                        </select>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* CSI division coverage */}
          <div className="rounded-xl border border-border">
            <button onClick={() => setOpenCsi((v) => !v)} className="flex w-full items-center justify-between px-4 py-3 font-semibold">
              <span>CSI Division Coverage ({exp.applicable_count} applicable)</span>
              {openCsi ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </button>
            {openCsi && (
              <div className="px-4 pb-4 space-y-1.5" data-testid="csi-coverage">
                {r.divisions.map((d) => {
                  const divSugs = suggestions.filter((s) => String(s.division) === String(d.code) && !unpricedItemSet.has((s.item || "").trim().toLowerCase()));
                  return (
                    <div key={d.code} className="rounded-lg bg-secondary/40 px-3 py-2" data-testid={`csi-div-${d.code}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <span className="text-sm font-medium">{d.code} · {d.name}</span>
                          {d.note && <div className="text-xs text-muted-foreground">{d.note}</div>}
                          {d.missing_detail?.length > 0 && d.status !== "NOT APPLICABLE" && (
                            <div className="text-[11px] text-amber-500">Missing: {d.missing_detail.join(", ")}</div>
                          )}
                        </div>
                        <span className={`shrink-0 inline-flex items-center rounded-full border px-2.5 py-0.5 text-[11px] font-bold ${STATUS_STYLE[d.status] || STATUS_STYLE["NEEDS REVIEW"]}`}>{d.status}</span>
                      </div>
                      {divSugs.map((sug, j) => {
                        const ckey = `div_${d.code}_${(sug.item || "").trim().toLowerCase().slice(0, 40)}`;
                        return (
                          <div key={j} className="mt-2 pt-2 border-t border-border/50" data-testid={`csi-sug-${d.code}-${j}`}>
                            <div className="text-xs font-semibold text-foreground">{sug.item}</div>
                            <PriceOptions sug={sug} appliedTier={appliedTier[ckey]} idPrefix={`csi-${d.code}-${j}`}
                              onApply={(opt) => applyOption(ckey, sug, opt)} onUnapply={() => unapply(ckey)} />
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Cost types */}
          <div className="rounded-xl border border-border p-4">
            <div className="font-semibold mb-2">Cost Breakdown by Type</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
              {[["Material", ct.material], ["Labor", ct.labor], ["Equipment", ct.equipment], ["Subcontractor", ct.subcontractor], ["Allowance", ct.allowance], ["Overhead", ct.overhead], ["Profit", ct.profit], ["Contingency", ct.contingency], ["Tax", ct.tax]].map(([l, v]) => (
                <div key={l} className="flex justify-between rounded-lg bg-secondary/40 px-3 py-2"><span className="text-muted-foreground">{l}</span><span className="font-mono">{money(v)}</span></div>
              ))}
            </div>
          </div>

          {/* Risk summary */}
          {(r.summary || r.commercial_exposure?.length > 0) && (
            <div className="rounded-xl border border-border p-4" data-testid="risk-summary">
              <div className="font-semibold flex items-center gap-2 mb-2"><AlertTriangle className="h-4 w-4 text-amber-500" /> Bid Review & Risk Summary</div>
              {r.summary && <p className="text-sm text-muted-foreground leading-relaxed">{r.summary}</p>}
              {r.commercial_exposure?.length > 0 && (
                <ul className="mt-2 space-y-1">
                  {r.commercial_exposure.map((c, i) => (
                    <li key={i} className="text-sm flex gap-2"><span className="text-amber-500">•</span><span className="text-muted-foreground">{c}</span></li>
                  ))}
                </ul>
              )}
              <p className="text-[11px] text-muted-foreground mt-3 italic">Language reflects potential change-order exposure — not a claim that a change order will occur.</p>
            </div>
          )}
        </>
      )}
    </div>
  );
}
